import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, Plus, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

const AlertSubscriptionsPage = () => {
  const { getAuthHeader } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newSubscription, setNewSubscription] = useState({
    category: '',
    min_price: '',
    max_price: '',
    location: ''
  });

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await api.getAlertSubscriptions(getAuthHeader());
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();

    const criteria = {};
    if (newSubscription.category) criteria.category = newSubscription.category;
    if (newSubscription.min_price) criteria.min_price = parseFloat(newSubscription.min_price);
    if (newSubscription.max_price) criteria.max_price = parseFloat(newSubscription.max_price);
    if (newSubscription.location) criteria.location = newSubscription.location;

    try {
      await api.createAlertSubscription({ criteria }, getAuthHeader());
      toast.success('Alert subscription created!');
      setShowDialog(false);
      setNewSubscription({ category: '', min_price: '', max_price: '', location: '' });
      loadSubscriptions();
    } catch (error) {
      toast.error('Failed to create subscription');
    }
  };

  const handleToggleSubscription = async (subscriptionId, active) => {
    try {
      await api.toggleAlertSubscription(subscriptionId, !active, getAuthHeader());
      toast.success(`Subscription ${!active ? 'activated' : 'deactivated'}`);
      loadSubscriptions();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleDeleteSubscription = async (subscriptionId) => {
    try {
      await api.deleteAlertSubscription(subscriptionId, getAuthHeader());
      toast.success('Subscription deleted');
      loadSubscriptions();
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  return (
    <div data-testid="alert-subscriptions-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold font-heading">
          Alert Subscriptions ({subscriptions.length})
        </h2>
        
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-subscription-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create Alert
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Email Alert Subscription</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSubscription} className="space-y-4">
              <p className="text-sm text-slate-600">
                You'll receive email notifications when new properties match your criteria.
              </p>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={newSubscription.category} 
                  onValueChange={(value) => setNewSubscription({ ...newSubscription, category: value })}
                >
                  <SelectTrigger data-testid="alert-category-select">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Categories</SelectItem>
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="min_price">Min Price</Label>
                  <Input
                    id="min_price"
                    type="number"
                    value={newSubscription.min_price}
                    onChange={(e) => setNewSubscription({ ...newSubscription, min_price: e.target.value })}
                    data-testid="alert-min-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="max_price">Max Price</Label>
                  <Input
                    id="max_price"
                    type="number"
                    value={newSubscription.max_price}
                    onChange={(e) => setNewSubscription({ ...newSubscription, max_price: e.target.value })}
                    data-testid="alert-max-price-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={newSubscription.location}
                  onChange={(e) => setNewSubscription({ ...newSubscription, location: e.target.value })}
                  placeholder="e.g., London, Manchester"
                  data-testid="alert-location-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="save-subscription-btn">
                Create Subscription
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-slate-600">Loading subscriptions...</p>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-sm">
          <p className="text-slate-600 mb-4">You don't have any alert subscriptions yet.</p>
          <p className="text-sm text-slate-500">Create an alert to get notified about new properties matching your criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {subscriptions.map((subscription) => (
            <div 
              key={subscription.subscription_id} 
              className="p-6 bg-white border border-slate-200 rounded-sm"
              data-testid={`subscription-${subscription.subscription_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {subscription.active ? (
                    <Bell className="h-5 w-5 text-[#F5A623]" />
                  ) : (
                    <BellOff className="h-5 w-5 text-slate-400" />
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">
                      {subscription.active ? 'Active Alert' : 'Inactive Alert'}
                    </h3>
                    <p className="text-sm text-slate-600">{subscription.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={subscription.active}
                    onCheckedChange={() => handleToggleSubscription(subscription.subscription_id, subscription.active)}
                    data-testid={`toggle-subscription-${subscription.subscription_id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteSubscription(subscription.subscription_id)}
                    data-testid={`delete-subscription-${subscription.subscription_id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <p className="font-medium mb-2">Alert Criteria:</p>
                {subscription.criteria.category && (
                  <p><span className="font-medium">Category:</span> {subscription.criteria.category}</p>
                )}
                {subscription.criteria.min_price && (
                  <p><span className="font-medium">Min Price:</span> £{subscription.criteria.min_price.toLocaleString()}</p>
                )}
                {subscription.criteria.max_price && (
                  <p><span className="font-medium">Max Price:</span> £{subscription.criteria.max_price.toLocaleString()}</p>
                )}
                {subscription.criteria.location && (
                  <p><span className="font-medium">Location:</span> {subscription.criteria.location}</p>
                )}
                {Object.keys(subscription.criteria).length === 0 && (
                  <p className="text-slate-500 italic">No specific criteria - will receive all new properties</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertSubscriptionsPage;
